import CheckmarkIcon from '@assets/icons/checkmark.svg';
import ChevronFilters from '@assets/icons/chevron-filters.svg';
import i18n, { DEFAULT_LOCALE } from '@/utils/i18n';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AVAILABILITY_DATE_FORMAT,
  ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP,
} from '@domain/fulfillment-settings/blocs/order-availability';
import {
  BaseActionSheet,
  DEFAULT_PADDING,
} from '@/components/base-action-sheet';
import { BORDER, TYPOGRAPHY } from '@/styles';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import {
  DaysOfWeek,
  FulfillmentName,
  FulfillmentType,
  GetNextDeliveryDateFor_SubscriptionQuery,
  GetNextDeliveryDateFor_SubscriptionQueryVariables,
  OrderFulfillmentAvailability,
} from '@/types';
import { formatDateByWeekday } from '@/utils/date';
import { FormExitConfirmation } from '@/components/form-exit-confirmation';
import { FormInputContainer } from '@/components/form';
import { GET_NEXT_DELIVERY_DATE_FOR_SUBSCRIPTION } from '@/screens/subscriptions/queries';
import { KeyboardAwareScrollView } from '@/components/with-keyboard-aware';
import { List } from '@/components/list';
import { ModalContainer } from '@/components/modal-container';
import { QuantitySelector } from '@/components/quantity-selector';
import { SelectInput } from '@/components/select-input';
import { SkeletonText } from '@/components/skeleton-text';
import { Text, View } from 'react-native';
import { translateFulfillmentType } from '@domain/fulfillment-settings/utils/translate-fulfillment-type';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useFormik } from 'formik';
import { useLazyQuery } from '@apollo/client';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useToasts } from '@/contexts/toast-context';

interface Frequency {
  title: string;
  value: number;
}

interface FulfillmentMethod {
  availability: OrderFulfillmentAvailability[];
  id: string;
  title: string;
  value: FulfillmentName;
}

interface OrderDay {
  date: string;
  title: string;
  value: DaysOfWeek;
}

export interface SubscriptionFormValues {
  frequencyDays: Frequency | null;
  fulfillmentMethod: FulfillmentMethod | null;
  orderDay: OrderDay | null;
  quantity: number;
}

interface Props {
  buttonLabel: string;
  initialValues: SubscriptionFormValues;
  loading: boolean;
  maximumQuantity: number | null;
  message: string;
  minimumQuantity: number | null;
  onClose: () => void;
  onSubmit: (values: SubscriptionFormValues) => Promise<void>;
  price: string;
  subscriptionUUID?: string;
  title: string;
}

export const SubscriptionForm: React.FC<Props> = ({
  buttonLabel,
  initialValues,
  loading,
  maximumQuantity,
  message,
  minimumQuantity,
  onClose,
  onSubmit,
  price,
  subscriptionUUID,
  title,
}) => {
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [selectingFrequency, setSelectingFrequency] = useState(false);
  const [selectingFulfillment, setSelectingFulfillment] = useState(false);
  const [selectingFulfillmentDay, setSelectingFulfillmentDay] = useState(false);
  const [nextPlacementDate, setNextPlacementDate] = useState('');

  const [styles, theme] = useStyles();
  const { showErrorToast } = useToasts();
  const { availableOrderFulfillments, selectableFulfillmentOptions } =
    useNewOrderContext();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();

  const { values, dirty, setFieldValue, submitForm } =
    useFormik<SubscriptionFormValues>({
      initialValues,
      onSubmit: async (values) => {
        await onSubmit(values);
      },
    });

  const displayName =
    values.fulfillmentMethod?.value === FulfillmentName.Delivery
      ? null
      : 'order';

  const notReadyToSubmit = useMemo(
    () => Object.values(values).some((value) => !value),
    [values],
  );

  const [getNextDeliveryDate, { loading: nextDeliveryDateLoading }] =
    useLazyQuery<
      GetNextDeliveryDateFor_SubscriptionQuery,
      GetNextDeliveryDateFor_SubscriptionQueryVariables
    >(GET_NEXT_DELIVERY_DATE_FOR_SUBSCRIPTION, {
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    (async () => {
      if (!notReadyToSubmit) {
        try {
          const { data } = await getNextDeliveryDate({
            variables: {
              frequencyDays: values.frequencyDays!.value,
              fulfillmentSettingsUUID: values.fulfillmentMethod!.id,
              orderDay: values.orderDay!.value,
              restaurantUUID,
              restaurantVPSubscriptionUUID: subscriptionUUID,
              supplierUUID,
            },
          });
          if (data?.getNextDeliveryDateForSubscriptionSettings) {
            const formattedDate = formatDateByWeekday(
              moment(
                data.getNextDeliveryDateForSubscriptionSettings
                  .next_placement_date,
              ),
            );
            setNextPlacementDate(formattedDate);
          }
        } catch {
          showErrorToast();
        }
      }
    })();
  }, [
    subscriptionUUID,
    values.orderDay,
    values.frequencyDays,
    values.fulfillmentMethod,
    getNextDeliveryDate,
    restaurantUUID,
    supplierUUID,
    showErrorToast,
    notReadyToSubmit,
  ]);

  const frequencyOptions: Frequency[] = useMemo(
    () =>
      [7, 14, 21, 28, 35, 42, 49, 56].map((days) => ({
        title: i18n.t(`frequency_${days}_days`),
        value: days,
      })),
    [],
  );

  const fulfillmentOptions: FulfillmentMethod[] = useMemo(
    () =>
      selectableFulfillmentOptions
        .filter((item) => item.fulfillment_name !== FulfillmentName.Pickup)
        .map((method) => ({
          availability: method.availability,
          id: method.fulfillment_settings_id,
          title:
            method.fulfillment_display_name ||
            ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP[
              method.fulfillment_name
            ],
          value: method.fulfillment_name,
        })),
    [selectableFulfillmentOptions],
  );

  const sortOrderDays = useCallback((a: OrderDay, b: OrderDay) => {
    const dayA = moment(a.date).day();
    const dayB = moment(b.date).day();

    // Handle Sundays
    const adjustedDayA = dayA === 0 ? 7 : dayA;
    const adjustedDayB = dayB === 0 ? 7 : dayB;
    return adjustedDayA - adjustedDayB;
  }, []);

  const availableDates = useMemo(() => {
    const selectedFulfillment = availableOrderFulfillments.find(
      (fulfillment) =>
        fulfillment.fulfillment_settings_id === values.fulfillmentMethod?.id,
    );

    if (
      selectedFulfillment?.fulfillment_type ===
      FulfillmentType.FulfillmentShippingOption
    ) {
      const orderDays: OrderDay[] = [];
      let currentDay = moment().add(1, 'day');

      for (let i = 0; i < 7; i++) {
        orderDays.push({
          date: currentDay.format(AVAILABILITY_DATE_FORMAT),
          title: currentDay.format('dddd'),
          value: currentDay
            .locale(DEFAULT_LOCALE)
            .format('dddd')
            .toUpperCase() as DaysOfWeek,
        });
        currentDay.add(1, 'day');
      }
      return orderDays.sort(sortOrderDays);
    }

    return (selectedFulfillment?.availability ?? [])
      .reduce<OrderDay[]>((acc, availability, index) => {
        // We want to include only first 7 days to build a weekDay selector (Monday-Sunday)
        if (index < 7) {
          let date = availability.date;
          if (moment(date).isSame(moment(), 'day')) {
            date = moment(date).add(7, 'days').format(AVAILABILITY_DATE_FORMAT);
          }
          const title = moment(date).format('dddd');
          const key = moment(date).locale(DEFAULT_LOCALE).format('dddd');
          if (!acc.find((item) => item.title === title)) {
            acc.push({
              date,
              title,
              value: key.toUpperCase() as DaysOfWeek,
            });
          }
        }
        return acc;
      }, [])
      .sort(sortOrderDays);
  }, [availableOrderFulfillments, sortOrderDays, values.fulfillmentMethod?.id]);

  const onBackPress = () => {
    if (dirty) {
      setShowExitConfirmation(true);
    } else {
      onClose();
    }
  };

  return (
    <ModalContainer
      navigationHeaderProps={{
        onBackPress,
      }}
    >
      <KeyboardAwareScrollView
        alwaysBounceVertical={false}
        contentContainerStyle={styles.contentContainer}
        keyboardDismissMode="on-drag"
      >
        <View style={styles.content}>
          <View collapsable={false}>
            <View style={styles.formHeader}>
              <Text style={styles.title}>{title}</Text>
              <Text style={[TYPOGRAPHY.p, styles.subtitle]}>{message}</Text>
            </View>

            <FormInputContainer>
              <Text style={styles.label}>{i18n.t('choose_amount')}</Text>
              <View style={styles.itemSubtextContainer}>
                <View style={styles.priceContainer}>
                  <Text
                    style={TYPOGRAPHY.medium}
                  >{`${i18n.t('unit_cost')} `}</Text>
                  <Text style={TYPOGRAPHY.pSemiBold}>{price}</Text>
                </View>
                <View style={styles.quantitySelector}>
                  <QuantitySelector
                    canDecrementToZero={false}
                    isFocused={false}
                    maxQuantity={maximumQuantity}
                    minQuantity={minimumQuantity}
                    onChange={(_, quantity) =>
                      setFieldValue('quantity', quantity)
                    }
                    quantity={values.quantity}
                  />
                </View>
              </View>
            </FormInputContainer>
            <FormInputContainer>
              <Text style={styles.label}>{i18n.t('fulfillment_method')}</Text>
              <SelectInput
                inputContainerStyle={BORDER.primary}
                label={!values.fulfillmentMethod ? i18n.t('choose_method') : ''}
                onPress={() => setSelectingFulfillment(true)}
                rightElement={
                  <ChevronFilters
                    fill={theme.colors.primary}
                    height={8}
                    width={14}
                  />
                }
                testID="subscription-form-fulfillment-method-select"
                value={values.fulfillmentMethod?.title ?? ''}
              />
            </FormInputContainer>
            <FormInputContainer>
              <Text style={styles.label}>
                {translateFulfillmentType(
                  values.fulfillmentMethod?.value ?? FulfillmentName.Delivery,
                  'subscription.frequency',
                  displayName,
                )}
              </Text>
              <SelectInput
                inputContainerStyle={BORDER.primary}
                label={!values.frequencyDays ? i18n.t('choose_frequency') : ''}
                onPress={() => setSelectingFrequency(true)}
                rightElement={
                  <ChevronFilters
                    fill={theme.colors.primary}
                    height={8}
                    width={14}
                  />
                }
                testID="subscription-form-frequency-select"
                value={values.frequencyDays?.title ?? ''}
              />
            </FormInputContainer>
            {!values.fulfillmentMethod ? null : (
              <FormInputContainer>
                <Text style={styles.label}>
                  {translateFulfillmentType(
                    values.fulfillmentMethod?.value ?? FulfillmentName.Delivery,
                    'subscription.day',
                    displayName,
                  )}
                </Text>
                <SelectInput
                  inputContainerStyle={BORDER.primary}
                  label={!values.orderDay ? i18n.t('choose_day') : ''}
                  onPress={() => setSelectingFulfillmentDay(true)}
                  rightElement={
                    <ChevronFilters
                      fill={theme.colors.primary}
                      height={8}
                      width={14}
                    />
                  }
                  testID="subscription-form-order-day-select"
                  value={values.orderDay?.title ?? ''}
                />
              </FormInputContainer>
            )}
          </View>
          <View collapsable={false}>
            {notReadyToSubmit ? null : nextDeliveryDateLoading ? (
              <View style={styles.nextDateLoaderWrapper}>
                <SkeletonText width="80%" />
              </View>
            ) : (
              <Text style={styles.caption}>
                {i18n.t('next_order_placed', {
                  date: nextPlacementDate,
                })}
              </Text>
            )}
            <Button
              disabled={notReadyToSubmit}
              loading={loading || nextDeliveryDateLoading}
              onPress={submitForm}
              trackingData={null} // we handle tracking in submitForm function
            >
              {buttonLabel}
            </Button>
          </View>
        </View>
        <BaseActionSheet
          isVisible={selectingFrequency}
          onClose={() => setSelectingFrequency(false)}
        >
          <List
            data={frequencyOptions}
            onSelect={(option) => {
              setFieldValue('frequencyDays', option);
              setSelectingFrequency(false);
            }}
            renderRightComponent={(option) => {
              if (option.value === values.frequencyDays?.value) {
                return (
                  <View style={styles.selectedIcon}>
                    <CheckmarkIcon
                      height={20}
                      stroke={theme.sk.color.icon.primary}
                      testID="selected-subscription-frequency-icon"
                      width={20}
                    />
                  </View>
                );
              }
            }}
            style={styles.list}
          />
        </BaseActionSheet>
        <BaseActionSheet
          isVisible={selectingFulfillment}
          onClose={() => setSelectingFulfillment(false)}
        >
          <List
            data={fulfillmentOptions}
            onSelect={(option) => {
              setFieldValue('fulfillmentMethod', option);
              setFieldValue('orderDay', null);
              setSelectingFulfillment(false);
            }}
            renderRightComponent={(option) => {
              if (option.value === values.fulfillmentMethod?.value) {
                return (
                  <View style={styles.selectedIcon}>
                    <CheckmarkIcon
                      height={20}
                      stroke={theme.sk.color.icon.primary}
                      testID="selected-subscription-frequency-icon"
                      width={20}
                    />
                  </View>
                );
              }
            }}
            style={styles.list}
          />
        </BaseActionSheet>
        <BaseActionSheet
          isVisible={selectingFulfillmentDay}
          onClose={() => setSelectingFulfillmentDay(false)}
        >
          <List
            data={availableDates}
            onSelect={(option) => {
              setFieldValue('orderDay', option);
              setSelectingFulfillmentDay(false);
            }}
            renderRightComponent={(option) => {
              if (option.value === values.orderDay?.value) {
                return (
                  <View style={styles.selectedIcon}>
                    <CheckmarkIcon
                      height={20}
                      stroke={theme.sk.color.icon.primary}
                      testID="selected-order-day-icon"
                      width={20}
                    />
                  </View>
                );
              }
            }}
            style={styles.list}
          />
        </BaseActionSheet>
      </KeyboardAwareScrollView>
      {showExitConfirmation ? (
        <FormExitConfirmation
          confirmTrackingData={{ name: 'subscription_screen.confirm_exit' }}
          message={i18n.t('exit_confirmation.message')}
          onConfirm={() => {
            onClose();
            setShowExitConfirmation(false);
          }}
          onSkip={() => setShowExitConfirmation(false)}
          skipTrackingData={{ name: 'subscription_screen.cancel_exit' }}
          title={i18n.t('exit_confirmation.title')}
        />
      ) : null}
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  caption: {
    color: theme.sk.color.text.secondary,
    fontFamily: theme.fonts.primary,
    fontSize: theme.fontSizes.small,
    lineHeight: 21,
    marginBottom: 16,
    textAlign: 'center',
  },
  content: {
    flexGrow: 1,
    gap: 32,
    justifyContent: 'space-between',
    paddingBottom: 36,
    paddingHorizontal: 24,
  },
  contentContainer: {
    flexGrow: 1,
  },
  formHeader: {
    marginBottom: 24,
  },
  itemSubtextContainer: {
    borderColor: theme.colors.stroke,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    height: 56,
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  label: {
    color: theme.sk.color.text.primary,
    fontFamily: theme.fonts.primarySemiBold,
    fontSize: theme.fontSizes.small,
    marginBottom: 8,
  },
  list: {
    marginHorizontal: -DEFAULT_PADDING,
    marginTop: 8,
  },
  nextDateLoaderWrapper: {
    alignItems: 'center',
    marginBottom: 16,
  },
  priceContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  quantitySelector: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  selectedIcon: {
    marginRight: 8,
  },
  subtitle: {
    color: theme.sk.color.text.tertiary,
    lineHeight: 24,
  },
  title: {
    color: theme.sk.color.text.primary,
    fontFamily: theme.fonts.primarySemiBold,
    fontSize: 21,
    lineHeight: 30,
    marginBottom: 4,
  },
}));
